/*
 * PaymentsPricing — public /pricing#payments page.
 *
 * Renders the canonical wallet rail pricing fetched from the backend
 * `/api/fees/schedule` endpoint (which reads from env-overridable
 * defaults in services.platform_revenue.DEFAULT_RATES). Single source
 * of truth — change the rate in env, this page + every in-app fee
 * preview updates together.
 *
 * Designed for: marketing transparency, sales conversations, in-app
 * "what does this cost?" preview drawers, and investor pitch reference.
 */
import { useEffect, useState } from "react";
import api from "../lib/api";
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  Zap,
  Clock,
  Building2,
  CreditCard,
  Sparkles,
  CheckCircle2,
  Loader2,
} from "lucide-react";

// Per-rail icon mapping — gives each row a small visual anchor.
const RAIL_ICON = {
  payin_ach_std:       Clock,
  payin_ach_sameday:   Clock,
  payin_wire_domestic: Building2,
  payin_rfp:           Zap,
  payin_card:          CreditCard,
  payout_ach_std:      Clock,
  payout_rtp:          Zap,
};

// What a competitor charges for the same rail. Hard-coded reference data
// — not fetched. Used purely for the side-by-side "vs your bank" column.
// Numbers verified against published 2026 rate cards from Chase Business,
// Wells Fargo Business, Bill.com, Stripe Connect, Routable.
const COMP_BENCHMARK = {
  payin_ach_std:       { label: "Bank avg: $0.40–1.00", note: "Chase, Wells, TD" },
  payin_ach_sameday:   { label: "Bank avg: $1.50–3.00", note: "premium speed at most banks" },
  payin_wire_domestic: { label: "Bank avg: $15",         note: "industry standard" },
  payin_rfp:           { label: "Routable: $5 RTP",       note: "competitive on RTP" },
  payin_card:          { label: "Stripe: 2.9% + $0.30",   note: "we keep it simple — flat %" },
  payout_ach_std:      { label: "Bill.com: $0.49",        note: "and most B2B banks $0.50+" },
  payout_rtp:          { label: "Stripe: 1.5% capped $15",note: "scales by plan" },
};

const PLAN_LABEL = {
  starter:    "Starter",
  growth:     "Growth",
  pro:        "Pro",
  enterprise: "Enterprise",
};

function formatRate(rail) {
  if (rail.shape === "flat") {
    return `$${rail.flat.toFixed(2)}`;
  }
  if (rail.shape === "pct") {
    return `${rail.pct.toFixed(2)}%`;
  }
  if (rail.shape === "tiered") {
    // Rendered specially by RailTable — this fallback is for any
    // unexpected place a tiered rate appears outside the table.
    const starter = rail.tiers?.find(t => t.plan === "starter");
    const growth  = rail.tiers?.find(t => t.plan === "growth");
    if (starter && growth && starter.pct === growth.pct) {
      return `${starter.pct.toFixed(2)}%`;
    }
    return `${starter?.pct?.toFixed(2) ?? "—"}% / ${growth?.pct?.toFixed(2) ?? "—"}%`;
  }
  if (rail.shape === "hybrid") {
    return `${rail.pct.toFixed(1)}% + $${rail.flat.toFixed(2)}`;
  }
  return "—";
}

export default function PaymentsPricing() {
  const [rails, setRails] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/fees/schedule");
        if (!cancelled) setRails(data?.rails || []);
      } catch (e) {
        if (!cancelled) setError("Couldn't load the fee schedule. Try refreshing.");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!rails && !error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center text-foreground/50 text-sm">
        <Loader2 className="w-4 h-4 mr-2 animate-spin"/>
        Loading pricing…
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center text-rose-700 text-sm">
        {error}
      </div>
    );
  }

  const payins  = rails.filter(r => r.direction === "pay-in");
  const payouts = rails.filter(r => r.direction === "pay-out");

  return (
    <div className="min-h-screen bg-background" data-testid="payments-pricing-page">
      {/* Hero */}
      <section className="px-6 py-16 sm:py-24 max-w-5xl mx-auto">
        <div className="text-[10.5px] uppercase tracking-[0.22em] mono text-foreground/55 mb-4">
          Wallet pricing · effective Feb 2026
        </div>
        <h1 className="display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
          Transparent rail fees.<br/>
          <span className="text-foreground/55">Cheaper than your business bank.</span>
        </h1>
        <p className="text-foreground/65 leading-relaxed mt-6 text-[15px] max-w-2xl">
          Every payment rail we offer, priced upfront. No hidden treasury-management
          subscriptions. No card-network cross-subsidies that show up as a 3% bill
          at month-end. What you see here is what we charge — full stop.
        </p>
      </section>

      {/* Pay-in section */}
      <section className="px-6 pb-12 max-w-5xl mx-auto">
        <SectionHeader
          icon={<ArrowDownToLine className="w-5 h-5"/>}
          title="Pay-in"
          subtitle="Buyers funding their verqo wallet to settle invoices."
        />
        <RailTable rails={payins}/>
      </section>

      {/* Pay-out section */}
      <section className="px-6 pb-16 max-w-5xl mx-auto">
        <SectionHeader
          icon={<ArrowUpFromLine className="w-5 h-5"/>}
          title="Pay-out"
          subtitle="Sellers withdrawing wallet balance to their external bank account."
        />
        <RailTable rails={payouts}/>
      </section>

      {/* The framing block — why we charge what we charge */}
      <section className="px-6 pb-24 max-w-5xl mx-auto">
        <div className="rounded-3xl border border-foreground/10 bg-foreground/[0.025] p-8 sm:p-10">
          <div className="text-[10.5px] uppercase tracking-[0.22em] mono text-foreground/55 mb-3">
            The unspoken comparison
          </div>
          <h2 className="display text-2xl sm:text-3xl font-semibold tracking-tight leading-tight">
            Most B2B fintechs benchmark against Stripe. We benchmark against your bank.
          </h2>
          <p className="text-foreground/65 leading-relaxed mt-4 max-w-2xl text-[14px]">
            Stripe is great for Shopify checkout. It charges 2.9% + $0.30 per card
            transaction — and that's where its margin comes from. We don't take a
            percentage of your sales. We charge for moving money the way your
            business bank does, just at lower rates and with everything bundled
            into one platform.
          </p>
          <div className="grid sm:grid-cols-3 gap-4 mt-7">
            <Stat label="Saved vs Chase Business" value="20–30%" hint="On every ACH and wire."/>
            <Stat label="No treasury sub" value="$0/mo"  hint="Chase charges $35–95."/>
            <Stat label="No card take-rate"   value="0%"    hint="vs Faire's 15–25%."/>
          </div>
        </div>
      </section>

      {/* Footer note */}
      <footer className="px-6 pb-12 max-w-5xl mx-auto text-[12px] text-foreground/55">
        <p>
          Fees may be updated with 30 days&apos; written notice. Wholesale partners
          on the Enterprise plan can negotiate custom rail pricing — contact
          sales for details.
        </p>
      </footer>
    </div>
  );
}

function SectionHeader({ icon, title, subtitle }) {
  return (
    <div className="flex items-start gap-3 mb-5">
      <div className="w-10 h-10 rounded-xl grid place-items-center bg-foreground text-background shrink-0">
        {icon}
      </div>
      <div>
        <h2 className="display text-2xl font-semibold tracking-tight">{title}</h2>
        <p className="text-[12.5px] text-foreground/60 mt-0.5">{subtitle}</p>
      </div>
    </div>
  );
}

function RailTable({ rails }) {
  return (
    <div
      className="rounded-2xl border border-foreground/10 bg-white overflow-hidden divide-y divide-foreground/10"
      data-testid="rail-table"
    >
      {rails.map((r) => {
        const Icon = RAIL_ICON[r.key] || Sparkles;
        const comp = COMP_BENCHMARK[r.key] || {};
        const isTiered = r.shape === "tiered";
        return (
          <div
            key={r.key}
            className="grid grid-cols-12 gap-4 items-center px-5 py-4 hover:bg-foreground/[0.02] transition-colors"
            data-testid={`rail-row-${r.key}`}
          >
            <div className="col-span-12 sm:col-span-5 flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg grid place-items-center bg-foreground/[0.06] shrink-0">
                <Icon className="w-4 h-4 text-foreground/70"/>
              </div>
              <div className="min-w-0">
                <div className="text-[13.5px] font-medium">{r.label}</div>
                <div className="text-[11.5px] text-foreground/55 mt-0.5 leading-snug">
                  {r.explainer}
                </div>
              </div>
            </div>
            <div className="col-span-6 sm:col-span-3">
              <div className="text-[10.5px] mono uppercase tracking-wider text-foreground/45 mb-0.5">
                verqo
              </div>
              {isTiered ? (
                <div className="space-y-0.5" data-testid={`rail-rate-${r.key}`}>
                  {r.tiers.filter(t => t.plan !== "enterprise" || t.pct !== r.tiers.find(x => x.plan === "growth")?.pct).map(t => (
                    <div key={t.plan} className="flex items-baseline gap-2">
                      <span className="text-[10px] mono uppercase tracking-wider text-foreground/45 w-14">
                        {PLAN_LABEL[t.plan]}
                      </span>
                      <span className="text-[16px] font-semibold tracking-tight">
                        {t.pct.toFixed(2)}%
                      </span>
                    </div>
                  ))}
                  <div className="text-[10px] text-foreground/45 mt-0.5">
                    Enterprise: same as Growth
                  </div>
                </div>
              ) : (
                <div className="text-[18px] font-semibold tracking-tight" data-testid={`rail-rate-${r.key}`}>
                  {formatRate(r)}
                </div>
              )}
            </div>
            <div className="col-span-6 sm:col-span-4">
              <div className="text-[10.5px] mono uppercase tracking-wider text-foreground/45 mb-0.5">
                vs market
              </div>
              <div className="text-[12.5px] text-foreground/75 font-medium">
                {comp.label || "—"}
              </div>
              {comp.note && (
                <div className="text-[10.5px] text-foreground/45 mt-0.5">
                  {comp.note}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Stat({ label, value, hint }) {
  return (
    <div className="rounded-xl bg-white border border-foreground/10 px-5 py-4">
      <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/55">
        {label}
      </div>
      <div className="display text-2xl font-semibold mt-1 inline-flex items-center gap-1.5">
        <CheckCircle2 className="w-4 h-4 text-emerald-600"/> {value}
      </div>
      <div className="text-[11px] text-foreground/55 mt-1">{hint}</div>
    </div>
  );
}
